import React, { useState } from "react";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import GridTable from "../../table/gridTable";
import Loader from "../../SharedLayouts/Loader";
import ExportCsvTable from "../../Export/ExportCsvTable";
import CustomButton from "../../Buttons/Button";
import IpoStatusChip from "./IpoStatusChip";
import RefundIpoSubscriptionModal from "../../modal/RefundIpoSubscriptionModal";
import { getCurrencySymbol } from "../../../Helper/getCurrencySymbol";

const formatMoney = (currency, value) =>
  value != null
    ? `${getCurrencySymbol(currency)} ${Number(value).toLocaleString()}`
    : "-";

const formatNumber = (value) =>
  value != null ? Number(value).toLocaleString() : "-";

// Admin rows across this API carry user_name / user_email at the top level
// (see Postman: orders, stock investors); user.{name,email} kept as fallback.
const subscriberName = (row) =>
  row?.user_name || row?.user?.name || `User #${row?.user_id ?? "-"}`;

const subscriberEmail = (row) => row?.user_email || row?.user?.email || "-";

// Ops can only cancel a subscription that hasn't been settled yet.
const REFUNDABLE_STATUSES = ["submitted", "processing"];

function IpoSubscriptionsTable({ ipoId, ipoStatus }) {
  const rows = useSelector((state) => state?.ipos?.ipoSubscriptions) || [];
  const loading = useSelector((state) => state?.ipos?.subscriptionsPending);

  const [selected, setSelected] = useState(null);
  const [showRefund, setShowRefund] = useState(false);

  const canRefund = (row) =>
    REFUNDABLE_STATUSES.includes(row?.status) && ipoStatus !== "listed";

  const columns = [
    { field: "reference", headerName: "Reference", width: 130 },
    {
      field: "user",
      headerName: "Subscriber",
      width: 220,
      renderCell: (params) => (
        <div className="leading-tight">
          <div className="font-[500]">{subscriberName(params.row)}</div>
          <div className="text-xs text-[#667085]">
            {subscriberEmail(params.row)}
          </div>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <IpoStatusChip status={params.row?.status} kind="subscription" />
      ),
    },
    {
      field: "shares_requested",
      headerName: "Requested",
      width: 110,
      renderCell: (params) => formatNumber(params.row?.shares_requested),
    },
    {
      field: "shares_allotted",
      headerName: "Allotted",
      width: 110,
      renderCell: (params) =>
        params.row?.allotment
          ? formatNumber(params.row?.allotment?.shares_allotted)
          : "—",
    },
    {
      field: "subscription_amount",
      headerName: "Subscription",
      width: 140,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.subscription_amount),
    },
    {
      field: "admin_fee",
      headerName: "Admin Fee",
      width: 120,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.admin_fee),
    },
    {
      field: "total_debit",
      headerName: "Total Debited",
      width: 140,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.total_debit),
    },
    {
      field: "refund_amount",
      headerName: "Refunded",
      width: 130,
      renderCell: (params) =>
        params.row?.refund_amount != null
          ? formatMoney(params.row?.currency, params.row?.refund_amount)
          : "—",
    },
    {
      field: "entry_mode",
      headerName: "Mode",
      width: 90,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.entry_mode || "-"}
        </span>
      ),
    },
    {
      field: "created_at",
      headerName: "Subscribed",
      width: 170,
      renderCell: (params) =>
        params.row?.created_at
          ? new Date(params.row.created_at).toLocaleString()
          : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (params) =>
        canRefund(params.row) ? (
          <CustomButton
            variant="outlined"
            customColor="#fff"
            children="Refund"
            width="80px"
            height="32px"
            borderColor="#B42318"
            color="#B42318"
            fz="12px"
            onClick={(e) => {
              e.stopPropagation();
              setSelected(params.row);
              setShowRefund(true);
            }}
          />
        ) : (
          <span className="text-xs text-[#98A2B3]">—</span>
        ),
    },
  ];

  const csvHeaders = [
    "Reference",
    "Subscriber",
    "Email",
    "Status",
    "Shares Requested",
    "Shares Allotted",
    "Subscription Amount",
    "Admin Fee",
    "Total Debited",
    "Refunded",
    "Entry Mode",
    "Transaction ID",
    "Subscribed At",
  ];

  const csvRows = rows.map((row) => [
    row?.reference,
    subscriberName(row),
    subscriberEmail(row),
    row?.status,
    row?.shares_requested ?? "-",
    row?.allotment?.shares_allotted ?? "-",
    formatMoney(row?.currency, row?.subscription_amount),
    formatMoney(row?.currency, row?.admin_fee),
    formatMoney(row?.currency, row?.total_debit),
    row?.refund_amount != null ? formatMoney(row?.currency, row?.refund_amount) : "-",
    row?.entry_mode,
    row?.transaction_id,
    row?.created_at ? new Date(row.created_at).toLocaleString() : "-",
  ]);

  return (
    <>
      <div className="flex justify-end items-center gap-3 mb-3">
        <ExportCsvTable
          headers={csvHeaders}
          rows={csvRows}
          filename={`IPO-${ipoId}-Subscriptions.csv`}
        />
      </div>

      {loading && rows?.length < 1 ? (
        <Loader />
      ) : (
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <GridTable
            rows={rows}
            columns={columns}
            type={"ipo-subscriptions"}
            noReport
            noSearch
          />
        </Box>
      )}

      <RefundIpoSubscriptionModal
        open={showRefund}
        subscription={selected}
        ipoId={ipoId}
        onClose={() => setShowRefund(false)}
      />
    </>
  );
}

export default IpoSubscriptionsTable;
